import React, {useEffect, useState} from 'react';
import {apiService} from '../services/api';
import {useAdmin} from './AdminContext';
import {formatNumber, formatPrice} from './adminUtils';

interface DashboardStats {
  totalUsers: number;
  activeUsers: number;
  totalListings: number;
  totalOrders: number;
  successfulOrders: number;
  totalRevenue: number;
  newUsers30d: number;
  newListings30d: number;
  newOrders30d: number;
  revenue30d: number;
}

const AdminDashboardPage: React.FC = () => {
  const {token} = useAdmin();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await apiService.adminGetDashboard(token);
        setStats(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Neuspesno ucitavanje dashboarda.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-semibold text-neutral-900">Dashboard</h2>
        <p className="mt-1 text-neutral-500">Brzi pregled poslovanja i aktivnosti.</p>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {Array.from({length: 4}).map((_, i) => (
            <div key={i} className="h-32 rounded-2xl bg-neutral-100" />
          ))}
        </div>
      ) : stats ? (
        <>
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-soft">
              <p className="text-sm text-neutral-500">Ukupno korisnika</p>
              <p className="mt-2 text-4xl font-semibold text-neutral-900">{formatNumber(stats.totalUsers)}</p>
              <p className="mt-2 text-sm text-neutral-400">Aktivni: {formatNumber(stats.activeUsers)}</p>
            </div>
            <div className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-soft">
              <p className="text-sm text-neutral-500">Nekretnine</p>
              <p className="mt-2 text-4xl font-semibold text-neutral-900">{formatNumber(stats.totalListings)}</p>
              <p className="mt-2 text-sm text-neutral-400">Novi 30d: {formatNumber(stats.newListings30d)}</p>
            </div>
            <div className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-soft">
              <p className="text-sm text-neutral-500">Orderi</p>
              <p className="mt-2 text-4xl font-semibold text-neutral-900">{formatNumber(stats.totalOrders)}</p>
              <p className="mt-2 text-sm text-neutral-400">Uspesni: {formatNumber(stats.successfulOrders)}</p>
            </div>
            <div className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-soft">
              <p className="text-sm text-neutral-500">Prihod</p>
              <p className="mt-2 text-4xl font-semibold text-neutral-900">{formatPrice(stats.totalRevenue, 'RSD')}</p>
              <p className="mt-2 text-sm text-neutral-400">30d: {formatPrice(stats.revenue30d, 'RSD')}</p>
            </div>
          </div>

          <div className="rounded-2xl border border-neutral-200 bg-white p-8 shadow-soft">
            <h3 className="text-xl font-semibold text-neutral-900">Poslednjih 30 dana</h3>
            <div className="mt-5 grid gap-4 md:grid-cols-3">
              <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-5">
                <p className="text-sm text-neutral-500">Novi korisnici</p>
                <p className="mt-2 text-3xl font-semibold text-neutral-900">{formatNumber(stats.newUsers30d)}</p>
              </div>
              <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-5">
                <p className="text-sm text-neutral-500">Novi orderi</p>
                <p className="mt-2 text-3xl font-semibold text-neutral-900">{formatNumber(stats.newOrders30d)}</p>
              </div>
              <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-5">
                <p className="text-sm text-neutral-500">Prihod 30d</p>
                <p className="mt-2 text-3xl font-semibold text-neutral-900">{formatPrice(stats.revenue30d, 'RSD')}</p>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="rounded-2xl border border-neutral-200 bg-white p-6 text-neutral-500 shadow-soft">
          Nema podataka.
        </div>
      )}
    </div>
  );
};

export default AdminDashboardPage;
